import React, { useEffect, useMemo, useState } from 'react';
import { useSessionRandom } from '../../hooks/useSeededRandom';
import { getString } from '@nackle/intl-tools';
import { Avatar, Skeleton, Spin, Progress } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { useAuthState } from '../../hooks/auth';
import { Navigation, Routes } from '../../navigation';
import { PaperIcon } from '@nackle/paper';
import Certificate from '../common/certificate';
import { localizeNumber } from '../../scripts/utils';


const RoadMap = ( { data, loading } ) => {

    const { seed, getSeededRandom } = useSessionRandom();

    const rand = useMemo( () => {
        if ( seed ) {
            return getSeededRandom();
        }
        return null;
    }, [ seed ] );

    const [ truncatePast, setTruncatePast ] = useState( false );
    const [ truncateFuture, setTruncateFuture ] = useState( false );


    function getLoadingItems( count ) {
        return [ ...Array( count ) ].map( ( x, i ) => ( {
            id: i,
            type: rand() < 0.25 ? 'curriculum' : 'course',
        } ) );
    }

    useEffect( () => {
        if ( data?.length > 0 ) {
            setTimeout( () => document.getElementsByClassName( 'road-profile' )[ 0 ]?.scrollIntoView( { container: 'nearest', inline: 'center' } ), 1000 );
        }
    }, [ data ] );
    

    /**
     * 
     * Map shows thusly:
     * Completed Items -> user logo -> In Progress Items -> Unstarted Items
     * 
     * Completed Items is sorted thusly:
     * least recently completed -> most recently completed
     * 
     * In Progress Items and Unstarted Items are sorted thusly:
     * curriculums (in dictated order) -> orphaned courses (in dictated order)
     * 
     * curriculum of status X is shown thusly:
     * courses that are in curriculum, that are in course list, that are of status X, in order of course list -> curriculum
     * 
     */
    const pastItems = useMemo( () => {
        if ( loading ) {return getLoadingItems( 8 );}
        if ( !data?.length > 0 ) {return [];}

        const sortValueGetter = ( d ) => {
            if ( d.type == 'curriculum' ) {
                return d.courses.sort( ( a, b ) => a.completedAtMs - b.completedAtMs ).at( -1 ).completedAtMs;
            } else {
                return d.completedAtMs;
            }
        };

        const items = [ ...data.filter( d => [ 'COMPLETED', 'COMPLETED_REVISED' ].includes( d.status ) && d.type == 'course' ), ...data.filter( d => d.progress == 100 && d.type == 'curriculum' ) ]
            .filter( c => !!c )
            .sort( ( a, b ) => sortValueGetter( a ) - sortValueGetter( b ) );

        if ( items.length > 8 ) {
            setTruncatePast( true );
            return items.slice( -8 );
        }

        return items;
    }, [ data ] );

    const futureItems = useMemo( () => {
        if ( loading ) {return getLoadingItems( 5 );}
        if ( !data?.length > 0 ) {return [];}

        function findAndPopChildren( arr, ids ) {

            const children = [];

            ids.forEach( id => {
                const i = arr.findIndex( co => co.id == id );
                if ( i != -1 ) {
                    children.push( arr[ i ] );
                    arr.splice( i, 1 );
                }
            } );

            children.sort( ( a, b ) => a.sortOrder - b.sortOrder );

            return children.map( ( c, i ) => ( { ...c, sibling: true } ) );
        }

        function buildFamily( curr, includeIP ) {
            const family = [ curr ];
            if ( !curr?.courses ) {return family;}

            const ids = curr.courses.map( c => c.id );

            family.unshift( ...findAndPopChildren( unstartedCourses, ids ) );
            if ( includeIP ) {
                family.unshift( ...findAndPopChildren( ipCourses, ids ) );
            }

            return family.filter( c => !!c );

        }

        // in progress courses sorted by weight
        const ipCourses = data.filter( d => [ 'IN_PROGRESS' ].includes( d.status ) && d.type == 'course' ).sort( ( a, b ) => a.weight - b.weight );
        const unstartedCourses = data.filter( d => [ 'NOT_STARTED' ].includes( d.status ) && d.type == 'course' ).sort( ( a, b ) => a.weight - b.weight );

        // in progress currs sorted by progress descending, secondary by weight
        const ipCurriculums = data.filter( d => d.progress < 100 & d.progress > 0 && d.type == 'curriculum' ).sort( ( a, b ) => a.weight - b.weight ).sort( ( a, b ) => b.progress - a.progress );
        const unstartedCurriculums = data.filter( d => d.progress == 0 && d.type == 'curriculum' ).sort( ( a, b ) => a.weight - b.weight );

        const items = ipCurriculums.flatMap( c => buildFamily( c, true ) ); // get in progress families
        items.push( ...ipCourses ); // get orphaned in progress courses
        items.push( ...unstartedCurriculums.flatMap( c => buildFamily( c, false ) ) ); // get unstarted families
        items.push( ...unstartedCourses ); // get orphaned unstarted courses

        if ( items.length > 8 ) {
            setTruncateFuture( true );
            return items.slice( 0, 8 );
        }

        return items;
    }, [ data ] );



    return <div className='road-map-section'>
        <h2 className='front-page-header highlight text-no'>{ getString( 'myJourney.map.title' ) }</h2>
        <div className={ classNames( 'road-map', { 'truncate-future': truncateFuture, 'truncate-past': truncatePast } ) }>
            { ( loading || pastItems?.length > 0 || futureItems?.length > 0 ) &&
                <>
                    { pastItems.flatMap( ( c, i ) => [ 
                        !truncatePast && i == 0 ? null : <TimelineBasic key={ `${ i - 1 }->${ i }` } loading={ loading } />, 
                        <RoadStop key={ c.id } item={ c } loading={ loading } /> 
                    ].filter( x => !!x ) ) }

                    <TimelineBasic key={ `${ pastItems.length - 1 }->${ pastItems.length }` } loading={ loading } />
                    <RoadProfile loading={ loading } />
                    <TimelineNext key={ `${ pastItems.length }->${ pastItems.length + 1 }` } loading={ loading } />

                    { futureItems.flatMap( ( c, i ) => [ 
                        <RoadStop key={ c.id } item={ c } loading={ loading } />, 
                        c.sibling || ( truncateFuture && i == futureItems.length - 1 ) ? <TimelineBasic key={ `${ pastItems.length + 1 + i }->${ pastItems.length + 2 + i }` } loading={ loading } /> : 
                            !truncateFuture && i == futureItems.length - 1 ? null : <TimelineNext key={ `${ pastItems.length + 1 + i }->${ pastItems.length + 2 + i }` } loading={ loading } /> 
                    ] ).filter( x => !!x ) }
                </>
            }
            { !loading && !pastItems?.length > 0 && !futureItems?.length > 0 &&
                <span className='text-it subtitle'>{ getString( 'courseList.empty' ) }</span>
            }
        </div>
    </div>;

};

const RoadProfile = ( ) => {
    return <div className='road-profile'>
        <Avatar size={ 64 } icon={ <UserOutlined /> } />
    </div>;
};

const TimelineLoading = () => {
    return <div className='timeline-holder'><div className='timeline timeline-loading' /></div>;
};

const TimelineNext = ( { loading } ) => {
    return loading ? <TimelineLoading /> : <div className='timeline-holder'><div className='timeline timeline-next highlight' /></div>;
};

const TimelineBasic = ( { loading } ) => {
    return loading ? <TimelineLoading /> : <div className='timeline-holder'><div className='timeline highlight' /></div>;
};

const RoadStop = ( { item, loading } ) => {

    const { claims } = useAuthState();

    function onClick() {
        if ( !loading && item ) {
            Navigation.push( item.type == 'course' ? Routes.CourseDetails.buildPath( item.id ) : Routes.CurriculumDetails.buildPath( item.id ) );
        }
    }

    return <div className={ classNames( 'road-stop', `type-${ item.type }` ) }>
        <span className={ classNames( 'stop-title', 'title', { clickable: !loading } ) } onClick={ onClick }>
            { loading ? <Skeleton active title={ false } paragraph={ { rows: item.type == 'course' ? 2 : 1, width: '65%' } } /> : item.name }
        </span>
        <Progress className={ classNames( 'road-dot highlight', { clickable: !loading } ) } onClick={ onClick } type='circle' percent={ loading ? 0 : item.progress } strokeColor={ 'currentcolor' } success={ { strokeColor: 'currentcolor' } } 
            format={ ( progress ) => 
                loading ? <Spin /> :
                progress == 100 ? <PaperIcon icon='Check' collection='antd-outlined' /> :
                progress > 0 && item.type == 'course' ? <PaperIcon icon='Hourglass' collection='antd-outlined' /> :
                `${ progress }%`
            } />
            
        { item.certificate?.hasCertificate && !loading &&
            <div className='certificate-detail stop-detail'>
                <Certificate isEarned={ item.certificate.earned } fetchCertificateUrl={ item.fetchCertificateUrl } />
            </div>
        }
        { item.totalScore > 0 && !loading &&
            <div className='points-detail stop-detail bordered-primary'>
                <h4>{ localizeNumber( item.totalScore, claims.locale ) }</h4>
            </div>
        }
    </div>;
};

export default RoadMap;